/*
Example usage:
  <lazy-video-loader
    src="path/to/video.mp4"
    poster="path/to/poster.jpg"
    width="640"
    height="360"
  ></lazy-video-loader>
*/

class LazyVideoLoader extends HTMLElement {
  constructor() {
    super();
    this.loaded = false;
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.innerHTML = `
      <style>
        video {
          display: block;
          width: 100%;
          height: 100%;
        }
      </style>
    `;
  }

  connectedCallback() {
    // Set up IntersectionObserver to trigger loading when close to viewport.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.loaded) {
            this.loadVideo();
          }
        });
      },
      { rootMargin: '200px' } // adjust as needed
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  loadVideo() {
    this.loaded = true;
    if (this.observer) this.observer.disconnect();

    // Create video element.
    const video = document.createElement('video');
    video.setAttribute('preload', 'none');
    video.setAttribute('playsinline', '');

    if (!window.accessibilityMode) {
      video.setAttribute('autoplay', '');
      // Autoplay usually requires muted video.
      video.muted = true;
    }

    if (this.getAttribute('show-controls') === 'true' || window.accessibilityMode) {
      video.setAttribute('controls', '');
    }

    // Optionally set poster.
    if (this.hasAttribute('poster')) {
      video.poster = this.getAttribute('poster');
    }
    // Optionally set dimensions.
    if (this.hasAttribute('width')) {
      video.width = this.getAttribute('width');
    }
    if (this.hasAttribute('height')) {
      video.height = this.getAttribute('height');
    }

    // Create and append the source element.
    const src = this.getAttribute('src');
    if (src) {
      const source = document.createElement('source');
      source.src = src;
      // Optionally set the type if provided.
      if (this.hasAttribute('type')) {
        source.type = this.getAttribute('type');
      }
      video.appendChild(source);
    }

    this.shadowRoot.appendChild(video);
  }
}

customElements.define('lazy-video-loader', LazyVideoLoader);
